// The session runtime engine. Manages the transcription lifecycle, collects utterances in memory, persists them to the backend (/api/utterances), keeps session timing/status updated, and maintains the Q/A workflow state
import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js";
import toast from "solid-toast";
import type { Utterance } from "../types/utterance";
import { SessionState } from "../types/SessionState";
import { CaseState, PriorityTag, SourceTag } from "../types/caseState";
import { mapQuestionToField } from "../utils/questionMappings";
import { SuggestedQuestion } from "../types/suggestquestions";
import { extractFields } from "../api/extractFields";
import { extractAnswer as extractAnswerApi } from "../api/extractAnswer";
import { translateBatch } from "../api/translateBatch";
import { createUtterance, updateUtterance } from "../api/utterances";
import { updateSession } from "../api/sessions";
import type { UtteranceEvent } from "../utils/eventBus";
import {
  InterviewQuestionsState,
  SessionMetadata,
} from "../utils/sessionMetadata";

import type {
  Transcriber,
  TranscriptionState,
} from "../transcription/Transcriber";
import { MicTranscriber } from "../transcription/MicTranscriber";
import { FileTranscriber } from "../transcription/FileTranscriber";
import { MultiDeviceTranscriber } from "../transcription/MultideviceTranscriber";
import { uiHash, uiLog } from "../utils/telemetry";
import { refineAiAskedAt } from "../features/transcription/transcriptBuilders";
import { SourceTypes } from "../constants/sourceTypes";

export function useTranscriptionSession(
  sessionId?: string,
  deps?: {
    transcriber?: Transcriber;
    getTranscriber?: () => Transcriber;
    interpreterConfig?: Accessor<{
      enabled: boolean;
      s1: string;
      s2: string;
    }>;
  },
) {
  // ---------- session state ----------
  const [session, setSession] = createSignal(SessionState.Idle);
  const [utterances, setUtts] = createSignal<Utterance[]>([]);
  const [seconds, setSeconds] = createSignal(0);
  const [error, setError] = createSignal("");
  const [startTime, setStartTime] = createSignal<Date | null>(null);
  const [endTime, setEndTime] = createSignal<Date | null>(null);
  const [targetLang, setTargetLang] = createSignal("");
  const [awaitingAnswer, setAwaiting] = createSignal(false);

  const [caseState, setCaseState] = createSignal<CaseState>({
    contactType: "",
    askedQuestions: [],
    answeredQuestions: [],
    dismissedQuestions: [],
  });
  const setContactType = (t: string) =>
    setCaseState((s): CaseState => ({ ...s, contactType: t }));

  // ---------- transcriber (DI) ----------
  // const transcriber: Transcriber = deps?.transcriber ?? new MicTranscriber();
  // ---------- transcriber (lazy DI) ----------
  let transcriber: Transcriber | null = null;

  type PublishUtterance = (evt: Omit<UtteranceEvent, "type">) => void;
  let publishUtterance: PublishUtterance | undefined;

  const interpreterConfig: Accessor<{
    enabled: boolean;
    s1: string;
    s2: string;
  }> =
    deps?.interpreterConfig ??
    (() => ({ enabled: false, s1: "en-US", s2: "" }));

  let offUtter: (() => void) | null = null;
  let offState: (() => void) | null = null;
  let offErr: (() => void) | null = null;

  function bindTranscriber(t: Transcriber) {
    // tear down old listeners if re-binding
    console.log("useTranscriptionSession.tsx bindTranscriber", t);
    try {
      offUtter?.();
      offState?.();
      offErr?.();
    } catch {}
    console.log("useTranscriptionSession.tsx bindTranscriber", t);
    offUtter = t.onUtterance((u) => {
      if (pendingQA && u.speaker === 2) {
        markQuestionAsked(pendingQA, u.text, {
          askedAt: pendingQAAskedAt,
        });
        pendingQA = null;
        pendingQAAskedAt = undefined;
        setAwaiting(false);
      }

      const utterance: Utterance = {
        speaker: u.speaker,
        text: u.text,
        start: u.start,
        end: u.end,
        lang: u.lang,
        translation: undefined,
        timestamp: u.timestamp || new Date().toISOString(),
        confidence: u.confidence,
      };
      (utterance as any).__from = "host";

      setUtts((prev) => [...prev, utterance]);
      autoSaveUtterance(utterance);
      console.log(`All uternaces and length: ${utterances().length}`);

      try {
        if (publishUtterance && sessionId) {
          const id = `${Date.now()}-${++utterSeq}`;
          publishUtterance({
            id,
            speaker: (utterance.speaker as 1 | 2) || 1,
            text: utterance.text,
            confidence: utterance.confidence,
            ts: Date.now(),
            lang: utterance.lang,
          });
          (utterance as any).__eventId = id;
        }
      } catch {}

      const interp = interpreterConfig();
      if (interp.enabled && interp.s1 && interp.s2) {
        const target = u.speaker === 1 ? interp.s2 : interp.s1;
        translateOneForInterpreter(utterance, target);
      }
    });

    offState = t.onState((s: TranscriptionState) => {
      if (s === "connecting") setSession(SessionState.Listening); // or a dedicated "Connecting"
      if (s === "listening") setSession(SessionState.Listening);
      if (s === "stopped") setSession(SessionState.Idle);
      if (s === "error") setSession(SessionState.Idle);
    });

    offErr = t.onError((e) => {
      console.error("useTranscriptionSession.ts transcriber error:", e);
      // Only reset session to Idle if it's a critical error. 
      // For file uploads, we want to keep the questions even if the "stream" ends abruptly.
      // setError(e.message);
      // setSession(SessionState.Idle);
    });
  }

  function ensureTranscriber(): Transcriber {
    if (transcriber) return transcriber;

    transcriber =
      deps?.transcriber ?? deps?.getTranscriber?.() ?? new MicTranscriber();

    bindTranscriber(transcriber);
    return transcriber;
  }

  function resetTranscriber() {
    try {
      offUtter?.();
      offState?.();
      offErr?.();
    } catch {}
    offUtter = offState = offErr = null;
    transcriber = null;
  }

  // ---------- locals ----------
  const queue: Utterance[] = [];
  let txTimer: ReturnType<typeof setTimeout>;
  let timerId: ReturnType<typeof setInterval> | null = null;
  let pendingQA: string | null = null;
  let pendingQAAskedAt: string | undefined;
  const inFlightAsked = new Set<string>();
  let isFirstUtterance = true;
  let metadataSaveTimer: ReturnType<typeof setTimeout>;
  let utterSeq = 0;

  // ---------- helpers: translate answers to English ----------
  async function translateToEnglish(
    text?: string,
  ): Promise<string | undefined> {
    try {
      if (!text || !text.trim()) return undefined;
      const [t] = await translateBatch([text], "en-US");
      return t;
    } catch {
      return undefined;
    }
  }

  // ---------- helpers: canonical English view ----------
  function englishText(u: Utterance): string {
    // Assumption: caseworker (speaker 1) speaks English; guest (speaker 2) translation is saved as English in interpreter mode
    // Fallback to original text if translation is unavailable
    if (!u) return "";
    return u.speaker === 1 ? u.text || "" : u.translation || u.text || "";
  }

  function normalizeForMatch(s: string): string {
    return (s || "")
      .toLowerCase()
      .replace(/[“”"'`]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function anchorAskedAt(questionText: string, approxAskedAt?: string) {
    try {
      const approx = approxAskedAt || new Date().toISOString();
      const anchored =
        refineAiAskedAt(utterances(), questionText, approx) || approx;
      const deltaMs = new Date(anchored).getTime() - new Date(approx).getTime();
      // Telemetry: helps measure how often anchoring shifts askedAt.
      if (Math.abs(deltaMs) >= 10_000) {
        uiLog("asked_at_anchor", {
          sessionId,
          question_sha: uiHash(questionText || ""),
          delta_ms: deltaMs,
        });
      }
      return anchored;
    } catch {
      return approxAskedAt || new Date().toISOString();
    }
  }

  // ---------- metadata save (debounced) ----------
  const saveInterviewQuestionsMetadata = (
    newState: CaseState,
    extra?: {
      suggestedQuestions?: SuggestedQuestion[];
      earlierSuggestions?: SuggestedQuestion[];
    },
  ) => {
    if (!sessionId) return;

    clearTimeout(metadataSaveTimer);
    metadataSaveTimer = setTimeout(() => {
      import("../api/sessions").then(({ updateSession, getSession }) => {
        getSession(sessionId)
          .then((s) => {
            const currentMetadata = (s.metadata ?? {}) as SessionMetadata;
            const currentIQ = currentMetadata.interviewQuestions ?? {};
            const incoming: SuggestedQuestion[] = (
              extra?.suggestedQuestions || []
            ).map((x) => ({
              id: x.id ?? x.question,
              question: x.question.trim(),
              fieldKey: (x as any).fieldKey ?? null,
              priority_level:
                (x as any).priority_level ?? ("Informational" as PriorityTag),
              importance: x.importance ?? "",
              source: (x as any).source ?? undefined,
            }));

            const updatedInterview: InterviewQuestionsState = {
              asked: newState.askedQuestions.slice(),
              answered: (newState.answeredQuestions || []).map((qa) => ({
                question: qa.question,
                fieldKey: (qa as any).fieldKey,
                answer: qa.answer,
                answer_en: (qa as any).answer_en,
                paraphrase: (qa as any).paraphrase,
                quote: (qa as any).quote,
                priority_level: qa.priority_level as PriorityTag | undefined,
                source: qa.source as any,
                origin: (qa as any).origin,
                method: (qa as any).method,
                answerStatus: (qa as any).answerStatus,
                confidence: qa.confidence,
                askedAt: qa.askedAt
                  ? new Date(qa.askedAt).toISOString()
                  : undefined,
                answeredAt: qa.answeredAt
                  ? new Date(qa.answeredAt).toISOString()
                  : undefined,
              })),
              dismissed: (newState.dismissedQuestions || []).map((d) =>
                typeof d === "string"
                  ? {
                      id: d,
                      question: d,
                      priority_level: "Informational" as PriorityTag,
                    }
                  : d,
              ),
              suggested:
                extra?.suggestedQuestions !== undefined
                  ? incoming
                  : currentIQ.suggested || [],
              earlier: Array.isArray(extra?.earlierSuggestions)
                ? (extra!.earlierSuggestions as any[])
                : (currentIQ as any).earlier || [],
            };

            const updatedMetadata: SessionMetadata = {
              ...(currentMetadata as SessionMetadata),
              interviewQuestions: updatedInterview,
            };
            return updateSession(sessionId, { metadata: updatedMetadata });
          })
          .catch((err) =>
            console.error("❌ Failed to save interview questions:", err),
          );
      });
    }, 500);
  };

  function persistInterviewMetadata(extra?: {
    suggestedQuestions?: SuggestedQuestion[];
    earlierSuggestions?: SuggestedQuestion[];
  }) {
    try {
      const snapshot = caseState();
      saveInterviewQuestionsMetadata(snapshot, extra);
    } catch (e) {
      console.error("persistInterviewMetadata failed:", e);
    }
  }

  // Immediate, non-debounced flush to avoid losing last-minute answers on stop/unload
  async function flushInterviewMetadataNow(extra?: {
    suggestedQuestions?: SuggestedQuestion[];
    earlierSuggestions?: SuggestedQuestion[];
  }) {
    if (!sessionId) return;
    try {
      const { updateSession, getSession } = await import("../api/sessions");
      const s = await getSession(sessionId);
      const currentMetadata = (s.metadata ?? {}) as SessionMetadata;
      const currentIQ = currentMetadata.interviewQuestions ?? {};
      const incoming: SuggestedQuestion[] = (
        extra?.suggestedQuestions || []
      ).map((x) => ({
        id: x.id ?? x.question,
        question: x.question.trim(),
        fieldKey: (x as any).fieldKey ?? null,
        priority_level:
          (x as any).priority_level ?? ("Informational" as PriorityTag),
        importance: x.importance ?? "",
        source: (x as any).source ?? undefined,
      }));

      const snapshot = caseState();
      const updatedInterview: InterviewQuestionsState = {
        asked: snapshot.askedQuestions.slice(),
        answered: (snapshot.answeredQuestions || []).map((qa) => ({
          question: qa.question,
          fieldKey: (qa as any).fieldKey,
          answer: qa.answer,
          answer_en: (qa as any).answer_en,
          paraphrase: (qa as any).paraphrase,
          quote: (qa as any).quote,
          priority_level: qa.priority_level as PriorityTag | undefined,
          source: qa.source as any,
          origin: (qa as any).origin,
          method: (qa as any).method,
          answerStatus: (qa as any).answerStatus,
          confidence: qa.confidence,
          askedAt: qa.askedAt ? new Date(qa.askedAt).toISOString() : undefined,
          answeredAt: qa.answeredAt
            ? new Date(qa.answeredAt).toISOString()
            : undefined,
        })),
        dismissed: (snapshot.dismissedQuestions || []).map((d) =>
          typeof d === "string"
            ? {
                id: d,
                question: d,
                priority_level: "Informational" as PriorityTag,
              }
            : d,
        ),
        // When suggestedQuestions is explicitly provided (even []), use it;
        // only fall back to DB value when caller didn't pass the field at all.
        suggested:
          extra?.suggestedQuestions !== undefined
            ? incoming
            : currentIQ.suggested || [],
        earlier: Array.isArray(extra?.earlierSuggestions)
          ? (extra!.earlierSuggestions as any[])
          : (currentIQ as any).earlier || [],
      };

      const updatedMetadata: SessionMetadata = {
        ...(currentMetadata as SessionMetadata),
        interviewQuestions: updatedInterview,
      };

      await updateSession(sessionId, { metadata: updatedMetadata });
    } catch (e) {
      console.error("flushInterviewMetadataNow failed:", e);
    }
  }

  // ---------- persistence of utterances ----------
  const autoSaveUtterance = async (utterance: Utterance) => {
    if (!sessionId) return;
    try {
      const saved = await createUtterance({
        sessionId,
        speaker: utterance.speaker,
        text: utterance.text,
        start: utterance.start,
        end: utterance.end,
        confidence: utterance.confidence,
        lang: utterance.lang,
        translation: utterance.translation,
      });
      utterance.id = saved.id;

      if (isFirstUtterance) {
        try {
          // Determine source_type based on transcriber type
          let sourceType: string = SourceTypes.LIVE_RECORDING; // default for mic
          if (transcriber instanceof FileTranscriber) {
            sourceType = SourceTypes.WAV_FILE;
          } else if (transcriber instanceof MultiDeviceTranscriber) {
            sourceType = SourceTypes.LIVE_RECORDING;
          }
          
          await updateSession(sessionId, {
            status: "in_progress",
            start_time: startTime() || new Date(),
            source_type: sourceType,
          });
          isFirstUtterance = false;
        } catch (statusError) {
          console.error("Failed to auto-update session status:", statusError);
        }
      }
    } catch (err) {
      console.error("Failed to auto-save utterance:", err);
    }
  };

  // ---------- interpreter one-off translation ----------
  async function translateOneForInterpreter(utt: Utterance, target: string) {
    try {
      if (utt.translation) return;
      const [text] = await translateBatch([utt.text], target);
      utt.translation = text;
      setUtts((prev) => prev.map((x) => (x === utt ? { ...utt } : x)));

      // broadcast translation
      try {
        const evtId = (utt as any).__eventId as string | undefined;
        if (publishUtterance && evtId) {
          publishUtterance({
            id: evtId,
            speaker: (utt.speaker as 1 | 2) || 1,
            translation: text,
            ts: Date.now(),
            lang: utt.lang,
          });
        }
      } catch {}

      if (utt.id && sessionId) {
        try {
          await updateUtterance(utt.id, { translation: text });
        } catch (e) {
          console.warn("Failed to persist translation (interp):", e);
        }
      } else {
        // late ID retry
        let attempts = 0;
        const t = setInterval(async () => {
          if (utt.id && sessionId) {
            clearInterval(t);
            try {
              await updateUtterance(utt.id, { translation: text });
            } catch (e) {
              console.warn("Late persist translation failed:", e);
            }
          }
          if (++attempts >= 5) clearInterval(t);
        }, 250);
      }
    } catch {
      try {
        const evtId = (utt as any).__eventId as string | undefined;
        if (publishUtterance && evtId) {
          publishUtterance({
            id: evtId,
            speaker: (utt.speaker as 1 | 2) || 1,
            translationError: true,
            ts: Date.now(),
          });
        }
      } catch {}
    }
  }

  // ---------- timer ----------
  const beginTimer = () => {
    const startTimeValue = new Date();
    setStartTime(startTimeValue);
    setSeconds(0);
    timerId = setInterval(() => setSeconds((s) => s + 1), 1000);

    if (sessionId) {
      updateSession(sessionId, { start_time: startTimeValue }).catch((err) =>
        console.error("Failed to auto-save start time:", err),
      );
    }
  };
  const clearTimer = () => {
    if (timerId) clearInterval(timerId);
    timerId = null;
  };
  const resetAll = () => {
    clearTimer();
    isFirstUtterance = true;
  };

  function addUtterance(u: Utterance) {
    const withTs = { ...u, timestamp: u.timestamp || new Date().toISOString() };
    setUtts((prev) => [...prev, withTs]);
  }

  createEffect(() => {
    if (interpreterConfig().enabled) return;

    if (!targetLang()) return;
    const uts = utterances();

    uts
      .filter(
        (u) => u.translation === undefined && u.lang && u.lang !== targetLang(),
      )
      .forEach((u) => queue.includes(u) || queue.push(u));

    clearTimeout(txTimer);
    if (queue.length === 0) return;

    txTimer = setTimeout(async () => {
      const batch = queue.splice(0);
      try {
        const translated = await translateBatch(
          batch.map((u) => u.text),
          targetLang()!,
        );
        for (let i = 0; i < batch.length; i++) {
          const utterance = batch[i];
          utterance.translation = translated[i];

          try {
            const evtId = (utterance as any).__eventId as string | undefined;
            if (publishUtterance && evtId) {
              publishUtterance({
                id: evtId,
                speaker: (utterance.speaker as 1 | 2) || 1,
                translation: translated[i],
                ts: Date.now(),
                lang: utterance.lang,
              });
            }
          } catch {}

          if (utterance.id && sessionId) {
            try {
              await updateUtterance(utterance.id, {
                translation: translated[i],
              });
            } catch (err) {
              console.error("Failed to save translation:", err);
            }
          }
        }
        setUtts([...utterances()]);
      } catch (e) {
        console.warn("translation failed", e);
        for (const utterance of batch) {
          try {
            const evtId = (utterance as any).__eventId as string | undefined;
            if (publishUtterance && evtId) {
              publishUtterance({
                id: evtId,
                speaker: (utterance.speaker as 1 | 2) || 1,
                translationError: true,
                ts: Date.now(),
              });
            }
          } catch {}
        }
      }
    }, 700);
  });

  function startListening(src: string, tgt: string | null) {
    console.log("started listeining useTranscriptionSession.tsxs");
    setTargetLang(tgt ?? "");
    const interp = interpreterConfig();
    if (interp.enabled && interp.s1 && interp.s2) {
      return _startListening([interp.s1, interp.s2]);
    }
    return _startListening([src]);
  }

  async function _startListening(langs: string[]) {
    console.log("useTranscriptionSession.ts _startListening", langs);
    if ([SessionState.Listening, SessionState.Stopping].includes(session())) {
      console.log("useTranscriptionSession.ts _startListening - already listening or stopping", session());
      return;
    }
    try {
      beginTimer();
      const t = ensureTranscriber();
      console.log("useTranscriptionSession.ts _startListening - calling t.start()");
      await t.start({ languages: langs });
      setSession(SessionState.Listening);
    } catch (e) {
      setError((e as Error).message);
      setSession(SessionState.Idle);
    }
  }

  async function stopAll(
    opts: { keepCallAlive?: boolean; suppressToast?: boolean } = {},
  ) {
    if ([SessionState.Idle, SessionState.Stopping].includes(session())) return;
    setSession(SessionState.Stopping);
    try {
      await transcriber?.stop();
    } finally {
      if (opts.keepCallAlive) {
        clearTimer();
        setSession(SessionState.Paused);
        return;
      }

      clearTimer();
      const endTimeValue = new Date();
      setEndTime(endTimeValue);

      if (sessionId && startTime()) {
        try {
          const durationSeconds = Math.floor(
            (endTimeValue.getTime() - startTime()!.getTime()) / 1000,
          );
          await updateSession(sessionId, {
            end_time: endTimeValue,
            duration_seconds: durationSeconds,
          });
        } catch (err) {
          console.error("Failed to auto-save session duration:", err);
        }
      }

      // Ensure latest interviewQuestions (including answer_en) are saved immediately
      await flushInterviewMetadataNow().catch(() => {});

      // (Optional) use fullText if needed in future
      // const fullText = utterances().map((u) => u.text).join(" ");

      if (!opts.suppressToast) {
        toast.success("Recording stopped successfully");
      }
      setSession(SessionState.Idle);
    }
  }

  onCleanup(() => {
    try {
      offUtter?.();
      offState?.();
      offErr?.();
    } catch {}
    stopAll({ suppressToast: true }).catch(() => {});
  });

  function setPublishUtterance(next?: PublishUtterance) {
    publishUtterance = next;
  }

  function markQuestionOffered(q: string) {
    setCaseState((prev) =>
      prev.askedQuestions.includes(q)
        ? prev
        : { ...prev, askedQuestions: [...prev.askedQuestions, q] },
    );
  }

  function saveSuggestedQuestions(_suggestions: SuggestedQuestion[]) {
    setCaseState((current) => {
      saveInterviewQuestionsMetadata(current);
      return current;
    });
  }

  function getLastNSeconds(seconds: number): string {
    if (!startTime()) return "";
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - seconds * 1000);
    return utterances()
      .filter((u) => {
        if (!u.timestamp) return true;
        const uttTime = new Date(u.timestamp);
        return uttTime >= cutoffTime;
      })
      .map((u) => `[Party ${u.speaker}]: ${englishText(u)}`)
      .join("\n");
  }

  function transcriptSince(iso: string, windowMs = 30_000) {
    const start = new Date(iso).getTime();
    const end = start + windowMs;
    return utterances()
      .filter((u) => {
        if (!u.timestamp) return false;
        const t = new Date(u.timestamp).getTime();
        return t >= start && t <= end;
      })
      .map((u) => `[Party ${u.speaker}]: ${englishText(u)}`)
      .join("\n");
  }

  function transcriptUntilNextInterviewer(iso: string, hardCapMs = 45_000) {
    const start = new Date(iso).getTime();
    const cap = start + hardCapMs;
    const lines: string[] = [];
    for (const u of utterances()) {
      if (!u.timestamp) continue;
      const t = new Date(u.timestamp).getTime();
      if (t < start) continue;
      if (t > cap) break;
      if (u.speaker === 1 && t > start) break;
      lines.push(`[Party ${u.speaker}]: ${englishText(u)}`);
    }
    const joined = lines.join("\n");
    return joined || transcriptSince(iso, 30_000);
  }

  async function extractAnswerWithAI(
    question: string,
    transcript: string,
    opts?: { strict?: boolean },
  ): Promise<
    | {
        answer?: string;
        paraphrase?: string;
        quote?: string;
        confidence?: number;
      }
    | undefined
  > {
    try {
      if (!transcript.trim()) return undefined;
      const result = await extractAnswerApi({
        question,
        transcript,
        strict: opts?.strict === undefined ? true : opts.strict,
      });
      return {
        answer: result?.answer || undefined,
        paraphrase: result?.paraphrase || undefined,
        quote: result?.quote || undefined,
        confidence:
          typeof result?.confidence === "number"
            ? result.confidence
            : undefined,
      };
    } catch {
      return undefined;
    }
  }

  function markQuestionAsked(
    q: string,
    answer?: string,
    meta?: {
      source?: SourceTag;
      origin?: "template" | "context" | "adhoc";
      confidence?: number;
      priority_level?: PriorityTag;
      askedAt?: string;
      quote?: string;
      paraphrase?: string;
      fieldKey?: string | null;
    },
  ) {
    const askedIso = meta?.askedAt || new Date().toISOString();
    setCaseState((prev): CaseState => {
      const existingIdx = prev.answeredQuestions.findIndex(
        (qa) => qa.question === q,
      );
      const isAlreadyAsked = prev.askedQuestions.includes(q);

      if (existingIdx >= 0) {
        const updated = [...prev.answeredQuestions];
        const current = updated[existingIdx] as any;
        const next = {
          ...current,
          answer: answer ?? current.answer,
          answer_en: current.answer_en,
          paraphrase:
            meta?.paraphrase !== undefined
              ? meta.paraphrase
              : (current as any).paraphrase,
          source: meta?.source ?? current.source,
          origin: meta?.origin ?? current.origin,
          fieldKey: meta?.fieldKey ?? current.fieldKey,
          confidence:
            typeof meta?.confidence === "number"
              ? meta.confidence
              : current.confidence,
          priority_level: meta?.priority_level ?? current.priority_level,
          quote:
            meta?.quote !== undefined ? meta.quote : (current as any).quote,
          answeredAt:
            answer && !current.answeredAt
              ? new Date().toISOString()
              : current.answeredAt,
          answerStatus:
            typeof answer === "string" && answer.trim()
              ? ("answered" as const)
              : current.answerStatus || ("extracting" as const),
        };
        const justFilled = !!answer && !current.answeredAt;
        if (justFilled) {
          uiLog("answered_fill", {
            sessionId,
            question_sha: uiHash(q || ""),
            source: meta?.source ?? current.source ?? "ai",
          });
        }

        updated[existingIdx] = next;
        const newState = { ...prev, answeredQuestions: updated };
        saveInterviewQuestionsMetadata(newState);
        // If we just received/updated an answer, backfill English translation
        if (answer) {
          translateToEnglish(answer).then((en) => {
            if (!en) return;
            setCaseState((cur): CaseState => {
              const upd = cur.answeredQuestions.map((qa) =>
                qa.question === q ? { ...qa, answer_en: en } : qa,
              );
              const st = { ...cur, answeredQuestions: upd };
              saveInterviewQuestionsMetadata(st);
              return st;
            });
          });
        }
        return newState;
      }

      if (!answer) {
        // Seed from slot extractor if a mapped field is already available
        try {
          const key = mapQuestionToField(q, caseState().contactType);
          const slotValue = key ? (caseState() as any)[key] : undefined;
          if (typeof slotValue === "string" && slotValue.trim()) {
            setCaseState((cur): CaseState => {
              const updatedAnswered = cur.answeredQuestions.map((qa) =>
                qa.question === q
                  ? {
                      ...qa,
                      answer: qa.answer || slotValue,
                      method: (qa as any).method || ("slot" as any),
                      answeredAt: qa.answeredAt || new Date().toISOString(),
                      answerStatus: "answered" as const,
                    }
                  : qa,
              );
              const st: CaseState = {
                ...cur,
                answeredQuestions: updatedAnswered,
              };
              saveInterviewQuestionsMetadata(st);
              return st;
            });
          }
        } catch {}

        const windowForExtraction = transcriptUntilNextInterviewer(
          askedIso,
          45_000,
        );

        // Helper: apply extraction result to caseState
        const applyExtraction = (
          ans: string,
          result: {
            paraphrase?: string;
            quote?: string;
            confidence?: number;
          },
        ) => {
          const conf =
            typeof result?.confidence === "number" ? result.confidence : 0;
          setCaseState((current): CaseState => {
            const updatedAnswered = current.answeredQuestions.map((qa) =>
              qa.question === q
                ? {
                    ...qa,
                    answer: qa.answer && qa.answer.trim() ? qa.answer : ans,
                    paraphrase:
                      result?.paraphrase ?? qa.paraphrase ?? undefined,
                    quote: result?.quote || qa.quote,
                    confidence: conf || qa.confidence,
                    method:
                      (qa as any).method ||
                      (qa.answer ? (qa as any).method : ("auto" as any)),
                    answeredAt: new Date().toISOString(),
                    answerStatus:
                      conf >= 0.5
                        ? ("answered" as const)
                        : ("inferred" as const),
                  }
                : qa,
            );
            const newState: CaseState = {
              ...current,
              answeredQuestions: updatedAnswered,
            };
            saveInterviewQuestionsMetadata(newState);
            translateToEnglish(ans).then((en) => {
              if (!en) return;
              setCaseState((cur): CaseState => {
                const upd = cur.answeredQuestions.map((qa) =>
                  qa.question === q ? { ...qa, answer_en: en } : qa,
                );
                const st = { ...cur, answeredQuestions: upd } as CaseState;
                saveInterviewQuestionsMetadata(st);
                return st;
              });
            });
            return newState;
          });
        };

        // Try window extraction first, fall back to full transcript
        extractAnswerWithAI(q, windowForExtraction, { strict: false })
          .then(async (result) => {
            const ans = result?.answer || "";
            const hasCandidate = !!ans && ans !== "No clear answer provided";

            if (hasCandidate) {
              applyExtraction(ans, result || {});
              return;
            }

            // Fallback: retry with full transcript if window extraction failed
            const fullTranscript = utterances()
              .map((u) => `[Party ${u.speaker}]: ${englishText(u)}`)
              .join("\n");
            if (!fullTranscript.trim()) {
              setCaseState((current): CaseState => {
                const updatedAnswered = current.answeredQuestions.map((qa) =>
                  qa.question === q
                    ? {
                        ...qa,
                        answerStatus: qa.answerStatus || ("no_answer" as const),
                      }
                    : qa,
                );
                const newState: CaseState = {
                  ...current,
                  answeredQuestions: updatedAnswered,
                };
                saveInterviewQuestionsMetadata(newState);
                return newState;
              });
              return;
            }

            const fallbackResult = await extractAnswerWithAI(
              q,
              fullTranscript,
              { strict: false },
            );
            const fallbackAns = fallbackResult?.answer || "";
            const hasFallback =
              !!fallbackAns && fallbackAns !== "No clear answer provided";

            if (hasFallback) {
              applyExtraction(fallbackAns, fallbackResult || {});
            } else {
              setCaseState((current): CaseState => {
                const updatedAnswered = current.answeredQuestions.map((qa) =>
                  qa.question === q
                    ? {
                        ...qa,
                        answerStatus: qa.answerStatus || ("no_answer" as const),
                      }
                    : qa,
                );
                const newState: CaseState = {
                  ...current,
                  answeredQuestions: updatedAnswered,
                };
                saveInterviewQuestionsMetadata(newState);
                return newState;
              });
            }
          })
          .catch((e) => console.error("AI extraction failed:", e));
      }

      const newState: CaseState = {
        ...prev,
        askedQuestions: isAlreadyAsked
          ? prev.askedQuestions
          : [...prev.askedQuestions, q],
        answeredQuestions: [
          ...prev.answeredQuestions,
          {
            question: q,
            fieldKey: meta?.fieldKey,
            answer,
            answer_en: undefined,
            source: meta?.source,
            origin: meta?.origin,
            confidence: meta?.confidence,
            priority_level: meta?.priority_level,
            askedAt: askedIso,
            paraphrase: meta?.paraphrase,
            quote: meta?.quote,
            answerStatus:
              typeof answer === "string" && answer.trim()
                ? ("answered" as const)
                : ("extracting" as const),
          },
        ],
      };
      uiLog("answered_add", {
        sessionId,
        question_sha: uiHash(q || ""),
        source: meta?.source ?? "manual",
        priority_level: meta?.priority_level ?? undefined,
        askedAt: askedIso,
        has_answer: typeof answer === "string" && !!answer.trim(),
      });
      saveInterviewQuestionsMetadata(newState);
      // Kick off translation for initial answer, if provided
      if (answer) {
        translateToEnglish(answer).then((en) => {
          if (!en) return;
          setCaseState((cur): CaseState => {
            const upd = cur.answeredQuestions.map((qa) =>
              qa.question === q ? { ...qa, answer_en: en } : qa,
            );
            const st = { ...cur, answeredQuestions: upd };
            saveInterviewQuestionsMetadata(st);
            return st;
          });
        });
      }
      return newState;
    });

    if (answer !== undefined) setAwaiting(false);
    inFlightAsked.delete(q);
  }

  function onAsk(
    question: string,
    opts?: {
      source?: SourceTag;
      answer?: string;
      confidence?: number;
      priority_level?: PriorityTag;
      askedAt?: string;
      fieldKey?: string | null;
    },
  ) {
    if (inFlightAsked.has(question)) return;
    inFlightAsked.add(question);

    const askedAtNow = opts?.askedAt || new Date().toISOString();
    const anchoredAskedAt = anchorAskedAt(question, askedAtNow);
    markQuestionAsked(question, opts?.answer, {
      source: opts?.source,
      confidence: opts?.confidence,
      priority_level: opts?.priority_level,
      fieldKey: opts?.fieldKey,
      askedAt: anchoredAskedAt,
    });
    if (!opts?.answer) {
      waitForAnswer(question, anchoredAskedAt);
    }
  }

  function waitForAnswer(q: string, askedAt?: string) {
    pendingQA = q;
    pendingQAAskedAt = askedAt;
    setAwaiting(true);
  }

  function markQuestionDismissed(q: SuggestedQuestion) {
    setCaseState((s): CaseState => {
      const newState = {
        ...s,
        dismissedQuestions: [...s.dismissedQuestions, q],
        askedQuestions: s.askedQuestions.filter((text) => text !== q.question),
      };
      saveInterviewQuestionsMetadata(newState);
      return newState;
    });
  }

  function restoreQuestion(q: SuggestedQuestion) {
    setCaseState((s): CaseState => {
      const newState = {
        ...s,
        dismissedQuestions: s.dismissedQuestions.filter(
          (d) => d.id !== q.id && d.question !== q.question,
        ),
      };
      saveInterviewQuestionsMetadata(newState);
      return newState;
    });
  }

  // ---------- live extraction (debounced) ----------
  let extractTimer: ReturnType<typeof setTimeout>;
  createEffect(() => {
    const uts = utterances();
    if (!uts.length) return;
    clearTimeout(extractTimer);
    extractTimer = setTimeout(async () => {
      const windowText = uts
        .slice(-5)
        .map((u) => u.text)
        .join(" ");
      try {
        const extracted = await extractFields(
          windowText,
          caseState().contactType ?? "Intake",
        );
        setCaseState(
          (prev): CaseState => ({
            ...prev,
            ...extracted,
            askedQuestions: prev.askedQuestions,
            answeredQuestions: prev.answeredQuestions,
            dismissedQuestions: prev.dismissedQuestions,
          }),
        );
      } catch (e) {
        console.error("extractFields error:", e);
      }
    }, 3000);
  });

  return {
    // state
    session,
    utterances,
    seconds,
    error,
    caseState,
    startTime,
    endTime,
    targetLang,
    awaitingAnswer,

    // setters / actions
    setCaseState,
    setContactType,
    setStartTime,
    setEndTime,
    setTargetLang,

    // controls
    startListening,
    stopAll,

    // QA / metadata
    addUtterance,
    markQuestionAsked,
    onAsk,
    markQuestionOffered,
    waitForAnswer,
    markQuestionDismissed,
    restoreQuestion,
    saveSuggestedQuestions,
    ingestBatch: async (uts: Utterance[], _fullText: string) => setUtts(uts),
    persistInterviewMetadata,
    flushInterviewMetadataNow,
    resetTranscriber,
    setPublishUtterance,
  };
}
